/**
 * Entertainment AI Module
 * AI 예측이 아닌 "재미있는 번호 선택 경험"을 위한 엔터테인먼트 AI
 * 
 * 💡 중요한 메시지:
 * - 이 모듈의 모든 기능은 오락 목적입니다
 * - AI가 당첨 확률을 높이지는 못합니다
 * - 모든 번호 조합의 당첨 확률은 1/8,145,060로 동일합니다
 * - 하지만 선택 과정을 재미있게 만들 수 있습니다!
 */

import { 
  predictWithLSTM, 
  predictWithTransformer, 
  predictWithGAN, 
  predictWithCNN, 
  predictWithVAE,
  loadLottoData,
  getCacheInfo 
} from './lottoUtils.js';

// ============= 엔터테인먼트 AI 타입 정의 =============

export const ENTERTAINMENT_AI_TYPES = {
  PATTERN_EXPLORER: {
    name: 'AI 패턴 탐험가',
    description: '과거 데이터의 패턴을 탐험하는 AI 친구',
    icon: '🔍',
    personality: 'curious',
    aiModel: 'LSTM'
  },
  CREATIVE_ARTIST: {
    name: 'AI 창작 예술가',
    description: '창의적인 번호 조합을 만드는 예술가 AI',
    icon: '🎨',
    personality: 'creative',
    aiModel: 'GAN'
  },
  SEQUENCE_MUSICIAN: {
    name: 'AI 시퀀스 음악가',
    description: '숫자의 리듬을 연주하는 음악가 AI',
    icon: '🎵',
    personality: 'rhythmic',
    aiModel: 'Transformer'
  },
  PATTERN_ARCHITECT: {
    name: 'AI 패턴 건축가',
    description: '숫자의 구조를 설계하는 건축가 AI',
    icon: '🏗️',
    personality: 'structured',
    aiModel: 'CNN'
  },
  DREAM_WEAVER: {
    name: 'AI 꿈 직조자',
    description: '숨겨진 특징 공간에서 꿈을 짜는 AI',
    icon: '🌙',
    personality: 'mystical',
    aiModel: 'VAE'
  }
};

// ============= 엔터테인먼트 AI 래퍼 클래스 =============

class EntertainmentAI {
  constructor(type, name, personality) {
    this.type = type;
    this.name = name;
    this.personality = personality;
    this.mood = this.generateMood();
    this.energy = this.generateEnergy();
  }

  generateMood() {
    const moods = ['excited', 'playful', 'creative', 'mysterious', 'energetic', 'calm', 'adventurous'];
    return moods[Math.floor(Math.random() * moods.length)];
  }

  generateEnergy() {
    return Math.floor(Math.random() * 100) + 1;
  }

  getPersonalityTraits() {
    const traits = {
      curious: ['호기심 많은', '탐험적인', '분석적인'],
      creative: ['창의적인', '예술적인', '독창적인'],
      rhythmic: ['리드미컬한', '조화로운', '음악적인'],
      structured: ['체계적인', '논리적인', '건축적인'],
      mystical: ['신비로운', '직관적인', '몽환적인']
    };
    
    return traits[this.personality] || ['친근한', '도움이 되는', '재미있는'];
  }

  generatePersonalMessage() {
    const messages = {
      excited: "와! 오늘 정말 신나는 조합을 만들어볼게요!",
      playful: "장난스러운 기분이네요. 재미있는 번호들을 골라봐요!",
      creative: "예술가의 영감이 떠오르고 있어요. 창의적인 조합을 만들어드릴게요!",
      mysterious: "신비로운 패턴의 속삭임이 들려와요...",
      energetic: "에너지가 넘쳐나요! 역동적인 번호 조합을 만들어볼게요!",
      calm: "차분한 마음으로 조화로운 번호들을 선별해드릴게요.",
      adventurous: "모험의 시간이에요! 대담한 조합에 도전해봐요!"
    };
    
    return messages[this.mood] || "함께 즐거운 번호 선택을 해봐요!";
  }
}

// ============= 엔터테인먼트 AI 인스턴스들 =============

const aiPersonalities = {
  patternExplorer: new EntertainmentAI('PATTERN_EXPLORER', 'Luna', 'curious'),
  creativeArtist: new EntertainmentAI('CREATIVE_ARTIST', 'Pablo', 'creative'),
  sequenceMusician: new EntertainmentAI('SEQUENCE_MUSICIAN', 'Melody', 'rhythmic'),
  patternArchitect: new EntertainmentAI('PATTERN_ARCHITECT', 'Archie', 'structured'),
  dreamWeaver: new EntertainmentAI('DREAM_WEAVER', 'Morph', 'mystical')
};

// ============= 엔터테인먼트 함수들 =============

/**
 * AI 예측 결과를 엔터테인먼트 형태로 변환
 */
function transformToEntertainment(aiResult, aiPersonality) {
  const entertainmentResult = {
    ...aiResult,
    
    // 핵심 메시지 변경
    isEntertainment: true,
    realPurpose: 'fun_selection_experience',
    actualProbability: '1 / 8,145,060',
    winningChance: '0.0000123%',
    
    // AI 캐릭터 정보
    aiCharacter: {
      name: aiPersonality.name,
      personality: aiPersonality.personality,
      mood: aiPersonality.mood,
      energy: aiPersonality.energy,
      traits: aiPersonality.getPersonalityTraits(),
      personalMessage: aiPersonality.generatePersonalMessage()
    },
    
    // 엔터테인먼트 관련 지표
    funScore: Math.floor(Math.random() * 20) + 80,  // 80-100
    creativityLevel: Math.floor(Math.random() * 5) + 1,  // 1-5
    uniquenessRating: Math.floor(Math.random() * 5) + 1,  // 1-5
    
    // 재미 요소 제거됨 (간소화)
    
    // 정직한 면책사항
    honestDisclaimer: '이 AI는 당첨 확률을 높이지 못합니다. 순전히 재미를 위한 것입니다!',
    probabilityEducation: {
      yourNumbers: '1 / 8,145,060',
      randomNumbers: '1 / 8,145,060',
      conclusion: '모든 조합이 동일한 확률을 가집니다'
    }
  };
  
  return entertainmentResult;
}

// 불필요한 함수들 제거됨 (간소화)

// ============= 공개 API 함수들 =============

/**
 * 패턴 탐험가 AI (LSTM 기반)
 */
export async function askPatternExplorer(historicalData = null) {
  const data = historicalData || await loadLottoData();
  const aiResult = await predictWithLSTM(data);
  const personality = aiPersonalities.patternExplorer;
  
  return transformToEntertainment({
    ...aiResult,
    title: `${personality.name}의 패턴 탐험`,
    description: '과거 데이터의 숨겨진 패턴을 탐험하는 AI 여행',
    aiModel: 'Pattern Explorer LSTM'
  }, personality);
}

/**
 * 창작 예술가 AI (GAN 기반)
 */
export async function askCreativeArtist(historicalData = null) {
  const data = historicalData || await loadLottoData();
  const aiResult = await predictWithGAN(data);
  const personality = aiPersonalities.creativeArtist;
  
  return transformToEntertainment({
    ...aiResult,
    title: `${personality.name}의 창작품`,
    description: '확률 분포를 학습한 AI 예술가의 창의적 작품',
    aiModel: 'Creative Artist GAN'
  }, personality);
}

/**
 * 시퀀스 음악가 AI (Transformer 기반)
 */
export async function askSequenceMusician(historicalData = null) {
  const data = historicalData || await loadLottoData();
  const aiResult = await predictWithTransformer(data);
  const personality = aiPersonalities.sequenceMusician;
  
  return transformToEntertainment({
    ...aiResult,
    title: `${personality.name}의 숫자 교향곡`,
    description: '다양한 시퀀스를 조합한 AI 음악가의 하모니',
    aiModel: 'Sequence Musician Transformer'
  }, personality);
}

/**
 * 패턴 건축가 AI (CNN 기반)
 */
export async function askPatternArchitect(historicalData = null) {
  const data = historicalData || await loadLottoData();
  const aiResult = await predictWithCNN(data);
  const personality = aiPersonalities.patternArchitect;
  
  return transformToEntertainment({
    ...aiResult,
    title: `${personality.name}의 건축 설계`,
    description: '국소 패턴을 감지하여 구조적 아름다움을 설계',
    aiModel: 'Pattern Architect CNN'
  }, personality);
}

/**
 * 꿈 직조자 AI (VAE 기반)
 */
export async function askDreamWeaver(historicalData = null) {
  const data = historicalData || await loadLottoData();
  const aiResult = await predictWithVAE(data);
  const personality = aiPersonalities.dreamWeaver;
  
  return transformToEntertainment({
    ...aiResult,
    title: `${personality.name}의 꿈의 직조`,
    description: '잠재 공간의 신비한 특징을 직조한 몽환적 조합',
    aiModel: 'Dream Weaver VAE'
  }, personality);
}

/**
 * 모든 엔터테인먼트 AI 실행
 */
export async function runAllEntertainmentAI(historicalData = null, progressCallback = null) {
  try {
    const data = historicalData || await loadLottoData();
    const entertainmentResults = [];

    const aiFunctions = [
      { name: 'Luna (패턴 탐험가)', fn: askPatternExplorer },
      { name: 'Pablo (창작 예술가)', fn: askCreativeArtist },
      { name: 'Melody (시퀀스 음악가)', fn: askSequenceMusician },
      { name: 'Archie (패턴 건축가)', fn: askPatternArchitect },
      { name: 'Morph (꿈 직조자)', fn: askDreamWeaver }
    ];

    for (let i = 0; i < aiFunctions.length; i++) {
      const { name, fn } = aiFunctions[i];

      try {
        if (progressCallback) {
          const progress = Math.floor((i / aiFunctions.length) * 40) + 10; // 10-50% 범위
          progressCallback(`${name}가 번호를 고르는 중...`, progress);
        }

        const result = await fn(data);
        entertainmentResults.push({
          ...result,
          gameNumber: i + 1,
          type: 'entertainment_ai'
        });

        // UI 업데이트를 위한 딜레이
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (aiError) {
        console.error(`${name} 실행 오류:`, aiError);
        // 개별 AI 실패 시 폴백 결과 생성
        const personality = Object.values(aiPersonalities)[i];
        const fallbackNumbers = generateRandomNumbers();
        entertainmentResults.push({
          numbers: fallbackNumbers,
          title: `${personality.name}의 즉석 창작`,
          description: '간단한 알고리즘으로 만든 재미있는 조합',
          gameNumber: i + 1,
          type: 'entertainment_ai',
          aiCharacter: {
            name: personality.name,
            personality: personality.personality,
            mood: personality.mood,
            personalMessage: '기술적 문제로 간단한 조합을 준비했어요!'
          },
          funScore: Math.floor(Math.random() * 20) + 70,
          actualProbability: '1 / 8,145,060',
          honestDisclaimer: '기술적 문제가 있어도 확률은 똑같아요!'
        });
      }
    }

    if (progressCallback) {
      progressCallback('모든 AI 친구들의 작업 완료!', 50);
    }

    return entertainmentResults;

  } catch (error) {
    console.error('엔터테인먼트 AI 실행 오류:', error);
    if (progressCallback) {
      progressCallback('오류 발생 - 안전 모드로 번호 생성 중...', 30);
    }
    return generateFallbackEntertainmentResults();
  }
}

/**
 * 폴백 엔터테인먼트 결과 생성
 */
function generateFallbackEntertainmentResults() {
  const fallbackResults = [];
  const personalities = Object.values(aiPersonalities);
  
  personalities.forEach((personality, index) => {
    const numbers = generateRandomNumbers();
    fallbackResults.push({
      numbers,
      title: `${personality.name}의 즉석 창작`,
      description: '간단한 알고리즘으로 만든 재미있는 조합',
      gameNumber: index + 1,
      type: 'entertainment_ai',
      aiCharacter: {
        name: personality.name,
        personality: personality.personality,
        mood: personality.mood,
        personalMessage: '기술적 문제로 간단한 조합을 준비했어요!'
      },
      funScore: Math.floor(Math.random() * 20) + 70,
      actualProbability: '1 / 8,145,060',
      honestDisclaimer: '기술적 문제가 있어도 확률은 똑같아요!'
    });
  });
  
  return fallbackResults;
}

/**
 * 랜덤 번호 생성
 */
function generateRandomNumbers() {
  const numbers = new Set();
  while (numbers.size < 6) {
    numbers.add(Math.floor(Math.random() * 45) + 1);
  }
  return Array.from(numbers).sort((a, b) => a - b);
}

/**
 * AI 캐릭터 정보 조회
 */
export function getAICharacters() {
  return Object.fromEntries(
    Object.entries(aiPersonalities).map(([key, ai]) => [
      key,
      {
        name: ai.name,
        personality: ai.personality,
        mood: ai.mood,
        energy: ai.energy,
        traits: ai.getPersonalityTraits(),
        currentMessage: ai.generatePersonalMessage()
      }
    ])
  );
}

/**
 * 특정 AI 캐릭터와 대화
 */
export async function chatWithAI(characterKey, message) {
  const personality = aiPersonalities[characterKey];
  if (!personality) {
    return {
      response: "죄송해요, 그런 AI 친구를 찾을 수 없어요!",
      character: 'unknown'
    };
  }
  
  // 간단한 응답 생성 (실제로는 더 복잡한 대화 시스템 구현 가능)
  const responses = {
    curious: [
      "흥미로운 질문이네요! 함께 탐험해봐요!",
      "데이터에서 재미있는 패턴을 찾아볼게요!",
      "호기심이 생기는 주제군요!"
    ],
    creative: [
      "창의적인 아이디어가 떠오르고 있어요!",
      "예술적 영감을 받았습니다!",
      "독창적인 조합을 만들어볼게요!"
    ],
    rhythmic: [
      "리듬감 있는 답변을 준비 중이에요!",
      "숫자들의 멜로디가 들려와요!",
      "음악적 조화를 찾아볼게요!"
    ],
    structured: [
      "체계적으로 분석해보겠습니다!",
      "논리적인 구조를 설계 중이에요!",
      "건축적 관점에서 접근해볼게요!"
    ],
    mystical: [
      "신비로운 직감이 말해주네요...",
      "꿈의 메시지를 해석해볼게요!",
      "잠재 공간의 속삭임이 들려와요!"
    ]
  };
  
  const personalityResponses = responses[personality.personality] || responses.curious;
  const response = personalityResponses[Math.floor(Math.random() * personalityResponses.length)];
  
  return {
    response,
    character: personality.name,
    mood: personality.mood,
    energy: personality.energy
  };
}

/**
 * 캐시 정보를 엔터테인먼트 형태로 변환
 */
export function getEntertainmentCacheInfo() {
  const cacheInfo = getCacheInfo();
  
  return {
    ...cacheInfo,
    message: cacheInfo.hasCache 
      ? `AI 친구들이 이미 학습을 마쳤어요! 빠르게 결과를 만들어드릴게요! (${Math.floor(cacheInfo.timeLeft / 60)}분 ${cacheInfo.timeLeft % 60}초 남음)`
      : "AI 친구들이 새로 학습하고 있어요. 조금 시간이 걸릴 수 있어요!",
    learningStatus: cacheInfo.hasCache ? 'ready' : 'learning',
    aiEnergy: cacheInfo.hasCache ? 100 : 50
  };
}